/** Single newsletter, read view, with edit affordances gated on role. */
import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import { useCanBrowse } from '../lib/viewmode'
import { RichTextView, PrayerText } from '../components/RichText'
import { MediaImage, MediaLightbox } from '../components/ImageUpload'
import {
  Card, Badge, Notice, Loading, ErrorState, ConfirmModal, Empty, BackTo,
  fmtInt, fmtDate, fmtDateTime, relDays, CountryLabel, PartnerLabel, useToast,
} from '../components/ui'
import { Icon } from '../components/Icon'
import { useRelabel, useCode } from '../lib/terms'
import { fundTypeLabel } from '../../shared/fundTypes.js'

/** Labelled "Updates" because that is what the sidebar and the list's header call it. */
const Back = () => <BackTo to="/newsletters">Updates</BackTo>

export default function NewsletterDetail({ user }) {
  const code = useCode()
  const t = useRelabel()
  const { id } = useParams()
  const canBrowse = useCanBrowse()
  const navigate = useNavigate()
  const toast = useToast()
  const query = useApi(['newsletter', id], `newsletters/${id}`)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState(null)

  const remove = async () => {
    setBusy(true)
    try {
      await api(`newsletters/${id}`, { method: 'DELETE' })
      toast.ok('Update deleted')
      navigate('/newsletters')
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  // The way out is offered even while the record is loading or failing to load —
  // a broken id is exactly when the reader most needs it.
  if (query.isLoading) {
    return (
      <PageHeader title="Update" back={<Back />}>
        <Loading />
      </PageHeader>
    )
  }
  if (query.isError) {
    return (
      <PageHeader title="Update" back={<Back />}>
        <ErrorState error={query.error} retry={query.refetch} />
      </PageHeader>
    )
  }

  const n = query.data.newsletter
  const { images, history, neighbours } = query.data

  return (
    <PageHeader
      title={fmtDate(n.published_on)}
      sub={
        <>
          {code(n.s4ca_code)} {n.partner_name} · {n.country_name ?? 'no country'} · record #{n.id}
        </>
      }
      back={<Back />}
      actions={
        <>
          {/* The Updates by Partner Unit report is staff-only, so this is not offered to
              an account that would land on "not part of your view". */}
          {canBrowse && (
            <Link className="btn btn-sm" to={`/reports/newsletters-by-partner?partner=${n.partner_id}`}>
              {t('All from Unit')}
            </Link>
          )}
          {/* The same "+ New" the Updates list, the dashboard and the Updates Overview
              carry. Landing here is the end of creating an update, which is the likeliest
              moment to want the next one — without it the way back to the form is a trip
              through the list. */}
          {user.caps.create && (
            <Link className="btn btn-sm btn-primary" to="/newsletters/new">
              + New Update
            </Link>
          )}
          {user.caps.update && (
            <Link className="btn btn-sm btn-primary" to={`/newsletters/${id}/edit`}>
              Edit
            </Link>
          )}
        </>
      }
    >
      <div className="report-layout nl-layout">
        <div className="col">
          <Card>
            <div className="col" style={{ gap: 6 }}>
              <PartnerLabel id={n.partner_id} code={n.s4ca_code} name={n.partner_name} />
              {/* The training school or project this update also reports on (migration
                  0032), under the unit that wrote it and labelled, because the two are
                  not the same claim: the first is the author, this one is a subject. The
                  label says which kind it is rather than saying "linked record", since
                  "Also about the North School (training school)" is what the reader is
                  actually looking at. */}
              {n.secondary_partner_id ? (
                <div className="row row-tight small">
                  <span className="subtle">Also about</span>
                  <PartnerLabel
                    id={n.secondary_partner_id}
                    code={n.secondary_s4ca_code}
                    name={n.secondary_name}
                  />
                  <Badge>{fundTypeLabel(n.secondary_fund_type).toLowerCase()}</Badge>
                </div>
              ) : null}
              <div className="row row-tight">
                {n.highlighted_need ? <Badge tone="warn"><Icon name="star" size={12} /> highlighted need</Badge> : null}
                {n.source === 'manual' ? <Badge>added in app</Badge> : <Badge>imported</Badge>}
              </div>
            </div>
            <hr />
            <dl className="kv">
              <dt>Date</dt>
              <dd className="mono">{n.published_on}</dd>
              <dt>Country</dt>
              <dd>
                <CountryLabel id={n.country_id} name={n.country_name} />
              </dd>
              <dt>Words</dt>
              <dd>{fmtInt(n.word_count)}</dd>
              <dt>Characters</dt>
              <dd>{fmtInt(n.body_chars)}</dd>
              <dt>Created</dt>
              <dd className="small">{fmtDateTime(n.created_at)}</dd>
              <dt>Updated</dt>
              <dd className="small">{fmtDateTime(n.updated_at)}</dd>
            </dl>

            {n.country_mismatch ? (
              <>
                <hr />
                <Notice tone="info" icon="info" title="Country differs from the registry">
                  This update records <strong>{n.country_name}</strong>, while the partner unit is
                  registered in <strong>{n.partner_country_name}</strong>. Both values are kept as
                  they appeared in the source data.
                </Notice>
              </>
            ) : null}
          </Card>

          {neighbours?.length ? (
            <Card title="Nearby from This Partner Unit" className="nl-nearby">
              <div className="col" style={{ gap: 4 }}>
                {neighbours.map((x) => (
                  <Link key={x.id} to={`/newsletters/${x.id}`} className="row small">
                    <span className="mono">{x.published_on}</span>
                    <span className="subtle">{relDays(x.published_on)}</span>
                  </Link>
                ))}
              </div>
            </Card>
          ) : null}
        </div>

        <div className="col">
          <Card title="Update">
            <RichTextView html={n.body_html} serif />
          </Card>

          <Card title="Prayer Requests">
            {n.has_prayer_request ? (
              <div className="nl-prayer">
                <PrayerText html={n.prayer_requests_html} text={n.prayer_requests} />
              </div>
            ) : (
              <Empty icon="prayer" title="No prayer requests recorded">
                {user.caps.update && (
                  <>
                    Open the <Link to={`/newsletters/${id}/edit`}>editor</Link> to extract them from
                    the update text with AI.
                  </>
                )}
              </Empty>
            )}
          </Card>

          {images?.length ? (
            <Card
              title="Images"
              sub={
                canBrowse
                  ? 'Click an image to see it full size'
                  : 'Obscured for the people in them — click to see one larger'
              }
            >
              <div className="media-grid">
                {images.map((img) => (
                  <button
                    className="media-item media-item-button"
                    key={img.id}
                    onClick={() => setViewing(img)}
                    title={img.alt_text || img.filename || 'View full size'}
                  >
                    <MediaImage id={img.id} alt={img.alt_text} />
                    <div className="media-meta subtle">{img.alt_text || img.filename}</div>
                  </button>
                ))}
              </div>
            </Card>
          ) : null}

          {/* Who edited what, and under which address — a staff record about staff,
              which is not part of a view-only account's view of the archive. */}
          {canBrowse && history?.length ? (
            <Card title="History" sub="Changes recorded against this record" className="nl-history">
              <div className="table-wrap">
                <table className="data compact">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Who</th>
                      <th>Action</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id}>
                        <td className="nowrap small">{fmtDateTime(h.created_at)}</td>
                        <td className="small">{h.user_email}</td>
                        <td>
                          <Badge>{h.action}</Badge>
                        </td>
                        <td className="small muted">{h.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : null}

          {user.caps.delete && (
            <div className="row nl-delete">
              <span className="spacer" />
              <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>
                Delete update
              </button>
            </div>
          )}
        </div>
      </div>

      <MediaLightbox image={viewing} onClose={() => setViewing(null)} />

      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        busy={busy}
        title="Delete this update?"
        body={
          <p>
            The update from <strong>{code(n.s4ca_code)}</strong> dated{' '}
            <strong>{fmtDate(n.published_on)}</strong> will be permanently removed.
          </p>
        }
      />
    </PageHeader>
  )
}
